
import { Component, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { GoalApiService, NamespaceInterface } from '@vendasta/ai-assistants';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyBadgeModule } from '@vendasta/galaxy/badge';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { GalaxyPageModule } from '@vendasta/galaxy/page';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyColumnDef, GalaxyDataSource, GalaxyTableModule } from '@vendasta/galaxy/table';
import { DateFormat } from '@vendasta/galaxy/utility/date-utils';
import { catchError, firstValueFrom } from 'rxjs';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { CapabilityProductIconComponent } from '../capabilities/capability-product-icon/capability-product-icon.component';
import { AiCapabilitiesPageDataSource, GoalInterfaceWithAssistants } from './ai-capabilities-page.data-source';

@Component({
  selector: 'ai-capabilities-page',
  imports: [
    GalaxyPageModule,
    TranslateModule,
    GalaxyTableModule,
    GalaxyAvatarModule,
    GalaxyBadgeModule,
    MatTableModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    GalaxyPipesModule,
    AiAssistantI18nModule,
    MatSort,
    MatSortHeader,
    CapabilityProductIconComponent,
  ],
  templateUrl: './ai-capabilities-page.component.html',
  styleUrl: './ai-capabilities-page.component.scss',
})
export class AiCapabilitiesPageComponent {
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly goalApiService = inject(GoalApiService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private readonly snackbarService = inject(SnackbarService);

  // Expose DateFormat enum for template use
  readonly DateFormat = DateFormat;

  columns: GalaxyColumnDef[] = [
    {
      id: 'name',
      title: 'AI_ASSISTANT.CAPABILITIES.COLUMNS.NAME',
      sortable: true,
    },
    {
      id: 'description',
      title: 'AI_ASSISTANT.CAPABILITIES.COLUMNS.DESCRIPTION',
      sortable: false,
    },
    {
      id: 'product',
      title: 'AI_ASSISTANT.CAPABILITIES.COLUMNS.PRODUCT',
      sortable: false,
    },
    {
      id: 'assignedAssistants',
      title: 'AI_ASSISTANT.CAPABILITIES.COLUMNS.ASSIGNED_ASSISTANTS',
      sortable: false,
    },
    {
      id: 'updated',
      title: 'AI_ASSISTANT.CAPABILITIES.COLUMNS.LAST_MODIFIED',
      sortable: true,
    },
    {
      id: 'actions',
      title: '',
      sortable: false,
    },
  ];

  // Create the data source to fetch all available capabilities for the namespace
  private capabilityDataSource = new AiCapabilitiesPageDataSource();
  dataSource = new GalaxyDataSource<GoalInterfaceWithAssistants, unknown, unknown>(this.capabilityDataSource);

  // Column IDs for the table
  displayedColumns: string[] = ['name', 'description', 'product', 'assignedAssistants', 'updated', 'actions'];

  // TrackBy function to help Angular efficiently track row changes
  trackByCapabilityId = (index: number, item: GoalInterfaceWithAssistants): string => {
    return item.id || index.toString();
  };

  onCapabilityRowClick(capability: GoalInterfaceWithAssistants, event?: Event): void {
    // Prevent default link behavior and handle navigation programmatically
    // This ensures proper Angular routing with relativeTo
    if (event) {
      event.preventDefault();
    }

    const namespaceType = this.getNamespaceType(capability.namespace);
    this.router.navigate([namespaceType, capability.id, 'edit'], {
      relativeTo: this.route,
    });
  }

  private getNamespaceType(namespace: NamespaceInterface | undefined): string {
    if (!namespace) return 'local';

    if (namespace.globalNamespace) return 'global';
    if (namespace.accountGroupNamespace) return 'local';
    if (namespace.partnerNamespace) return 'local';
    if (namespace.systemNamespace) return 'system';

    return 'local'; // default fallback
  }

  getCapabilityEditUrl(capability: GoalInterfaceWithAssistants): string {
    const namespaceType = this.getNamespaceType(capability.namespace);
    return `${namespaceType}/${capability.id}/edit`;
  }

  createCapability(): void {
    this.router.navigate(['create'], {
      relativeTo: this.route,
    });
  }

  sortData(sort: Sort): void {
    this.dataSource.setSorting([
      {
        active: sort.active,
        direction: sort.direction,
      } as MatSort,
    ]);
  }

  async deleteCapability(capability: GoalInterfaceWithAssistants): Promise<void> {
    if (!capability.isCustom) {
      return;
    }

    const confirmed = await firstValueFrom(
      this.confirmationModal.openModal({
        type: 'warn',
        title: 'AI_ASSISTANT.CAPABILITIES.DELETE.TITLE',
        message: 'AI_ASSISTANT.CAPABILITIES.DELETE.CONFIRMATION',
        confirmButtonText: 'AI_ASSISTANT.SHARED.DELETE',
      }),
    );

    if (!confirmed) {
      return;
    }

    await firstValueFrom(
      this.goalApiService
        .delete({
          id: capability.id,
          namespace: capability.namespace,
          removeFromAssistants: true,
        })
        .pipe(
          catchError((error) => {
            this.snackbarService.openErrorSnack('AI_ASSISTANT.CAPABILITIES.DELETE.ERROR');
            throw error;
          }),
        ),
    );

    this.snackbarService.openSuccessSnack('AI_ASSISTANT.CAPABILITIES.DELETE.SUCCESS');

    // Remove the capability from the current data using the data source
    this.capabilityDataSource.removeCapability(capability.id, this.dataSource);
  }
}
